import axios, { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  ComparisonEnvironment,
  ShadowTestResultResponse,
  Stage,
} from "../lusca-search-api/generated-src";
import { ShadowTestApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

interface UseShadowTestResultParams {
  stage: Stage;
  transactionId: string | undefined;
}

const responseMessage = (data: unknown) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const body = data as { error?: unknown; message?: unknown };
  if (typeof body.message === "string") {
    return body.message;
  }
  return typeof body.error === "string" ? body.error : undefined;
};

const isMissingResult = (
  error: unknown,
  transactionId: string,
  comparisonEnvironment?: ComparisonEnvironment
) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const expectedMessage =
    comparisonEnvironment === ComparisonEnvironment.Beta
      ? `No BETA shadow test result found for: ${transactionId}`
      : `No shadow test result found for: ${transactionId}`;
  return responseMessage(error.response.data) === expectedMessage;
};

export default function useShadowTestResult({
  stage,
  transactionId,
}: UseShadowTestResultParams) {
  const shadowTestApi = ShadowTestApiFactory();

  const resultQuery = (comparisonEnvironment?: ComparisonEnvironment) => ({
    queryKey: [
      "shadowTestResult",
      stage,
      transactionId,
      comparisonEnvironment ?? ComparisonEnvironment.Gamma,
    ],
    queryFn: async () => {
      if (!transactionId) {
        throw new Error("Transaction ID is required");
      }
      try {
        const { data }: AxiosResponse<ShadowTestResultResponse> =
          await shadowTestApi.getShadowTestResult(
            stage,
            transactionId,
            comparisonEnvironment
          );
        return data;
      } catch (error) {
        if (isMissingResult(error, transactionId, comparisonEnvironment)) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!transactionId && !!stage,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const gammaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery()
  );
  const betaQuery = useQuery<ShadowTestResultResponse | null, Error>(
    resultQuery(ComparisonEnvironment.Beta)
  );

  const refetch = async () => {
    await Promise.all([gammaQuery.refetch(), betaQuery.refetch()]);
  };

  return {
    gammaResult: gammaQuery.data,
    betaResult: betaQuery.data,
    gammaIsLoading: gammaQuery.isLoading,
    betaIsLoading: betaQuery.isLoading,
    gammaIsFetching: gammaQuery.isFetching,
    betaIsFetching: betaQuery.isFetching,
    gammaError: gammaQuery.error || null,
    betaError: betaQuery.error || null,
    refetch,
  };
}
